import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Package, AlertTriangle, TrendingDown, ShoppingCart } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Dashboard = () => {
  const [stats, setStats] = useState({
    total_products: 0,
    low_stock_count: 0,
    reorder_needed_count: 0,
    critical_stock_count: 0,
  });
  const [reorderProducts, setReorderProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, reorderRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/products/reorder/needed`),
      ]);
      setStats(statsRes.data);
      setReorderProducts(reorderRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: "Total Products",
      value: stats.total_products,
      icon: Package,
      color: "text-blue-600",
      bg: "bg-blue-50",
      testId: "total-products-stat"
    },
    {
      title: "Reorder Needed",
      value: stats.reorder_needed_count,
      icon: ShoppingCart,
      color: "text-orange-600",
      bg: "bg-orange-50",
      testId: "reorder-needed-stat"
    },
    {
      title: "Low Stock Items",
      value: stats.low_stock_count,
      icon: TrendingDown,
      color: "text-yellow-600",
      bg: "bg-yellow-50",
      testId: "low-stock-stat"
    },
    {
      title: "Critical Stock",
      value: stats.critical_stock_count,
      icon: AlertTriangle,
      color: "text-red-600",
      bg: "bg-red-50",
      testId: "critical-stock-stat"
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Monitor your inventory levels and reorder requirements</p>
      </div>


      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="stat-card" data-testid={stat.testId}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  {stat.title}
                </CardTitle>
                <div className={`${stat.bg} p-2 rounded-lg`}>
                  <Icon className={`h-5 w-5 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card data-testid="reorder-alerts-section">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">Products Requiring Reorder</CardTitle>
          <p className="text-sm text-gray-600 mt-1">
            Items below reorder level that need immediate attention
          </p>
        </CardHeader>
        <CardContent>
          {reorderProducts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Package className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p>All products are well stocked!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reorderProducts.map((product) => {
                const orderQuantity = product.maximum_level - product.current_stock;
                const isCritical = product.current_stock <= product.minimum_level;
                
                return (
                  <div
                    key={product.id}
                    data-testid={`reorder-product-${product.id}`}
                    className={`p-4 rounded-lg border ${
                      isCritical ? "critical-stock" : "low-stock"
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{product.name}</h3>
                        <p className="text-sm text-gray-600">{product.category}</p>
                        {isCritical && (
                          <span className="inline-flex items-center mt-2 text-xs font-medium text-red-800">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            CRITICAL - Below minimum level
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Current Stock</div>
                        <div className="text-2xl font-bold text-gray-900">
                          {product.current_stock} {product.unit}
                        </div>
                        <div className="mt-2 text-sm">
                          <span className="text-gray-600">Order: </span>
                          <span className="font-bold text-purple-600" data-testid={`order-quantity-${product.id}`}>
                            {orderQuantity} {product.unit}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-4 text-sm text-gray-600">
                      <span>Min: {product.minimum_level}</span>
                      <span>Reorder: {product.reorder_level}</span>
                      <span>Max: {product.maximum_level}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;